import { useEffect } from "react";
import { Link } from "react-router-dom";

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  readTime: string;
  heroImgSrc: string;
  heroImgAlt: string;
  description: string;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  subtitle,
  readTime,
  heroImgSrc,
  heroImgAlt,
  description,
  children,
}: ArticleLayoutProps) {
  const lastModified = new Date().toISOString().split("T")[0];
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuy.wiki";
  const pagePath = typeof window !== "undefined" ? window.location.pathname : "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: title,
            description: description,
            image: heroImgSrc,
            datePublished: "2026-06-25",
            dateModified: lastModified,
            author: {
              "@type": "Organization",
              name: "ACBuy Wiki",
            },
            publisher: {
              "@type": "Organization",
              name: "ACBuy Wiki",
              logo: {
                "@type": "ImageObject",
                url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": siteUrl + pagePath,
            },
          }),
        }}
      />
      {/* Article Navbar */}
      <nav className="w-full bg-background-50/95 backdrop-blur-md border-b border-background-200/60 sticky top-0 z-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading text-2xl font-bold text-foreground-950 tracking-tight">
              ACBuy
            </span>
            <span className="text-sm text-foreground-500 font-light tracking-wider mt-1">
              Wiki
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/acbuyspreadsheet-guide"
              className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-book-open-line mr-1"></i>
              Shopping Guide
            </Link>
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="max-w-[800px] mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-700 transition-colors">
            Home
          </Link>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <Link to="/acbuyspreadsheet-guide" className="hover:text-foreground-700 transition-colors">
            Guide
          </Link>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <span className="text-foreground-600 font-medium">{subtitle}</span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <span className="inline-block px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-full mb-4 tracking-wide">
            ACBuy Wiki · 2026 Edition
          </span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {title}
          </h1>
          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span>Updated: {lastModified}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{readTime} min read</span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden bg-background-100/50 mb-10">
          <img
            src={heroImgSrc}
            alt={heroImgAlt}
            title={title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Body */}
        <div className="prose-custom">{children}</div>

        {/* Related Articles */}
        <div className="mt-14 pt-8 border-t border-background-200/60">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
            More ACBuy Spreadsheet Articles
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/acbuyspreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors group"
            >
              <i className="ri-file-text-line text-primary-500 text-lg"></i>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 whitespace-nowrap">
                Complete Guide
              </span>
            </Link>
            <Link
              to="/acbuyspreadsheet-tips"
              className="flex items-center gap-3 px-4 py-3 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors group"
            >
              <i className="ri-lightbulb-line text-accent-500 text-lg"></i>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 whitespace-nowrap">
                Tips and Tricks
              </span>
            </Link>
            <Link
              to="/acbuyspreadsheet-safety"
              className="flex items-center gap-3 px-4 py-3 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors group"
            >
              <i className="ri-shield-check-line text-secondary-600 text-lg"></i>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 whitespace-nowrap">
                Safety and Trust
              </span>
            </Link>
            <Link
              to="/acbuyspreadsheet-pricing"
              className="flex items-center gap-3 px-4 py-3 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors group"
            >
              <i className="ri-money-dollar-circle-line text-secondary-600 text-lg"></i>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 whitespace-nowrap">
                Pricing Guide
              </span>
            </Link>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-12 pt-8 border-t border-background-200/60">
          <p className="text-sm text-foreground-500 text-center mb-4">
            This article was last updated on {lastModified}. For the latest product data,
            visit the{" "}
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              ACBuy Wiki homepage
            </Link>{" "}
            or go directly to{" "}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              kaspreadsheet.com
            </a>.
          </p>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-foreground-950 text-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading text-xl font-bold text-background-50 tracking-tight">
                ACBuy Wiki
              </span>
            </Link>
            <div className="flex items-center gap-6 flex-wrap">
              <Link to="/" className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors">
                Home
              </Link>
              <Link to="/acbuyspreadsheet-guide" className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors">
                Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors"
              >
                Main Platform
              </a>
            </div>
          </div>
          <p className="text-xs text-background-50/30 text-center mt-4">
            &copy; {new Date().getFullYear()} ACBuy Wiki. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}